import { Fragment, useContext, useEffect, useRef, useState } from "react"
import PublicContext from "store/context/PublicContext"
import { Breadcrumb, TitleScreen, Paginator, Table, Button, Modal, Select, Checkbox } from "components/Core"
import SearchBarFilter from "components/SearchBarFilter"
import cn from "classnames"
import { get_data_table_presale_sgs } from "overrides/theme/entel/base/helpers/data_tables"
import { parsed_filters_on_search } from "helpers/data_tables"
import usePaginationTable from "overrides/theme/entel/base/hooks/usePaginationTable"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useFiltersTable from "hooks/useFiltersTable"
import usePreRender from "hooks/usePreRender"
import { format_date } from "helpers/datetime_helper"
import useOrchResources from "hooks/customs/useOrchResources"
import { getAvailableDatesByDeliveryType } from "helpers/schedule_helper"
import UseShippingGroupActions from "hooks/customs/UseShippingGroupActions"
import Swal from "sweetalert2"

const ListScreen = () => {
  const { user: current_user } = useContext(PublicContext)
  const [current_order, set_current_order] = useState<any>()
  const [date_selected, set_date_selected] = useState<any>()
  const [tid_schedule, set_tid_schedule] = useState<any>()
  const [available_dates, set_available_dates] = useState<any>()
  const [sgs_checked, set_sgs_checked] = useState<any>([])
  const { _color } = usePreRender()
  const { postAddTraceShippingGroup, postMultipleExecuteActionShippingGroup } = UseShippingGroupActions()
  const { set_loading_app, post_get_schedule_orders_action } = useOrchResources()
  const { loading_app, data, search_osrm_action } = useSearchMiddlewareOSRM({ entity: "shipping_groups" })
  const modal_reschedule_ref = useRef<any>(null)
  const modal_cancel_ref = useRef<any>(null)

  const {
    current_data,
    current_page,
    handle_page_change,
    handle_per_page,
    per_page,
    total_records,
    total_pages,
    sort,
    set_sort,
  } = usePaginationTable({
    data,
  })

  const { filters_table } = useFiltersTable({
    table_name: "presale_shippinggroups",
    onFilterApplied: () => handle_page_change(0),
  })

  const exec_req = () =>
    search_osrm_action({
      from: per_page * current_page,
      size: per_page,
      filters: { ...parsed_filters_on_search(filters_table.state), isPresale: true },
      // filters: { ...parsed_filters_on_search(filters_table.state) },
      sortBy: { orderCreationDate: sort },
    })

  useEffect(() => {
    exec_req()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_page, per_page, filters_table.state, sort])

  const [columns, data_table_sgs] = get_data_table_presale_sgs(current_data)

  const selectRescheduleOrder = async (_data: any) => {
    set_current_order(_data)
    set_date_selected("")
    set_tid_schedule("")
    modal_reschedule_ref?.current?.open()
    let _body: any = {
      channel: _data?.channel,
      originalOderId: _data?.salesChannelId,
      shippingType: [_data?.shippingType],
      items: _data?.source?.items?.map((__item: any) => ({ sku: __item?.sku, quantity: __item?.quantity })),
    }
    if (_data?.shippingType === "HD") {
      _body = {
        ..._body,
        shippingAddress: {
          country: _data?.shippingAddress?.country,
          region: _data?.shippingAddress?.region,
          location: {
            // ..._data?.shippingAddress,
            province: _data?.shippingAddress.province || "",
            commune: _data?.shippingAddress.commune || "",
            address: `${_data?.shippingAddress.addressName || ""} ${_data?.shippingAddress.addressNumber || ""}`,
            latitude: _data?.shippingAddress.georeference.latitude || "",
            longitude: _data?.shippingAddress.georeference.longitude || "",
          },
        },
        contact: {
          name: _data?.shippingAddress.contact.name || "",
          email: _data?.shippingAddress.contact.email || "",
          phone: _data?.shippingAddress.contact.phone || "",
        },
      }
    }
    set_loading_app(true)
    const _res: any = await post_get_schedule_orders_action({ body: _body })
    set_tid_schedule(_res?.data?.transactionId)
    set_loading_app(false)
    if (_res?.data?.message?.shippingType) {
      const dates = getAvailableDatesByDeliveryType(_res.data.message, _data?.deliveryType)
      set_available_dates(dates)
    }
  }

  const confirmRescheduleOrder = async () => {
    try {
      let data_req = {
        id: "RESCHEDULE",
        description: date_selected,
        custom: {
          transactionIdSchedule: tid_schedule,
          executer: {
            name: current_user?.email,
            id: "Omnix XMonitor",
          },
        },
      }
      set_loading_app(true)
      const res: any = await postAddTraceShippingGroup(current_order?.salesChannelId, data_req)
      if (res?.data?.code === 200) {
        modal_reschedule_ref?.current?.close()
        Swal.fire({
          title: "Hecho",
          text: `Orden de preventa ${current_order?.salesChannelId} reagendada al ${format_date(date_selected)}`,
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#002EFE",
        })
      }
      set_loading_app(false)
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text: JSON.stringify(error?.response?.data?.message),
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#FD0049",
      })
      set_loading_app(false)
    }
  }

  const handle_all_sgs = (e: any) => {
    const checked = e.target.checked
    if (checked) {
      const all_sgs = data_table_sgs?.map((sg: any) => sg?.salesChannelId)
      set_sgs_checked(all_sgs)
    } else {
      set_sgs_checked([])
    }
  }

  const handle_select_sg = (e: any, sg: any) => {
    const checked = e.target.checked
    if (checked) {
      set_sgs_checked((prev: any) => [...prev, sg?.salesChannelId])
    } else {
      set_sgs_checked((prev: any) => prev.filter((id: any) => id !== sg?.salesChannelId))
    }
  }

  const confirmCancelOrders = async () => {
    try {
      let data_req = {
        entityId: sgs_checked,
        action: "CANCEL",
        custom: {
          reason: "CLIENT_REQUEST_PRESALE",
          description: "Cancelación de PREVENTA",
          executer: {
            name: current_user?.email,
            id: "Omnix XMonitor",
          },
        },
      }
      set_loading_app(true)
      const res: any = await postMultipleExecuteActionShippingGroup(data_req)
      if (res?.data?.code === 200) {
        modal_reschedule_ref?.current?.close()
        Swal.fire({
          title: "Hecho",
          text: `Ordenes canceladas!`,
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#002EFE",
        })
        set_sgs_checked([])
        modal_cancel_ref.current.close()
        exec_req()
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text: JSON.stringify(error?.response?.data?.message),
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#FD0049",
      })
    } finally {
      set_loading_app(false)
    }
  }

  return (
    <Fragment>
      <div className="w-full h-full flex flex-col items-center justify-start pb-8">
        <Breadcrumb data={[{ name: "Listado de grupo de envíos preventa", href: "/presale/sgs" }]} />
        <div className="w-full flex flex-col justify-center items-center py-4 px-6">
          <TitleScreen title="listado de grupo de envíos preventa" />
          {/* <p>SourceListScreen</p> */}
        </div>
        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex flex-col lg:flex-row justify-between items-end lg:items-center gap-4 px-6">
            <SearchBarFilter handle_page_change={handle_page_change} />
            {sgs_checked?.length ? (
              <Button
                label="Cancelar selección"
                onClick={() => modal_cancel_ref?.current?.open()}
                className={cn("px-4 truncate animate-pulse z-10 hover:animate-none", `bg_${_color}`)}
              />
            ) : null}
          </div>
          <div className="w-full flex flex-col gap-2 px-6">
            <Paginator
              current_page={current_page}
              handle_page_change={handle_page_change}
              per_page={per_page}
              total_records={total_records}
              total_pages={total_pages}
              handle_per_page={handle_per_page}
              sort={sort}
              set_sort={set_sort}
            />
            <div className="w-full overflow-x-auto">
              <Table>
                <Table.Head>
                  <Table.HeadCell>
                    <Checkbox
                      checked={sgs_checked?.length === data_table_sgs?.length}
                      onSelect={handle_all_sgs}
                      name="select_all_sg"
                    />
                  </Table.HeadCell>
                  {columns?.map((key: string, i: number) => (
                    <Table.HeadCell key={i}>{key}</Table.HeadCell>
                  ))}
                  <Table.HeadCell>Opciones</Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {!loading_app && data_table_sgs?.length ? (
                    data_table_sgs?.map((sg: any, i: number) => (
                      <Table.Row key={i}>
                        <Table.Cell>
                          <Checkbox
                            checked={sgs_checked?.includes(sg?.salesChannelId)}
                            onSelect={(e: any) => handle_select_sg(e, sg)}
                            name="select_sg"
                          />
                        </Table.Cell>
                        <Table.Cell mainCol>
                          <a href={`/sgs/${sg?.id}`} className="text-[#F9004D] underline">
                            {sg?.salesChannelId}
                          </a>
                        </Table.Cell>
                        <Table.Cell>{sg?.channel}</Table.Cell>
                        <Table.Cell>{sg?.orderCreationDate}</Table.Cell>
                        <Table.Cell>{sg?.estimatedDeliveryDate}</Table.Cell>
                        <Table.Cell>{sg?.currentStatus}</Table.Cell>
                        <Table.Cell>{sg?.shippingType}</Table.Cell>
                        <Table.Cell>{sg?.deliveryType}</Table.Cell>
                        <Table.Cell className="py-0 px-2">
                          <Button label="Reagendar" className="p-0" onClick={() => selectRescheduleOrder(sg?.sg)} />
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell>{loading_app ? "Espere..." : "Sin resultados"}</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          </div>
        </div>
      </div>
      <Modal ref={modal_reschedule_ref}>
        <div className="flex flex-col w-96">
          <div className={cn("w-full text-center rounded-t-xl p-2", `bg_${_color}`)}>
            <h4 className="text-lg font-medium uppercase text-white">{`Reagendar órden ${current_order?.salesChannelId}`}</h4>
          </div>
          <div className="w-full flex flex-col rounded-b-xl gap-4 p-4">
            {loading_app ? (
              <label className="text-md text-gray-700">Espere...</label>
            ) : (
              <div className="w-full flex flex-col gap-2">
                <label className="text-md text-gray-700">Fecha de reagendamiento</label>
                <Select
                  label="Fechas para reagendar"
                  options={available_dates}
                  onChange={set_date_selected}
                  value={date_selected}
                />
                <div className="pt-2">
                  <label className="text-sm text-gray-700">
                    Fecha de entrega actual: {format_date(current_order?.estimatedDeliveryDate)}
                  </label>
                </div>
              </div>
            )}
            <div className="flex flex-row items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => modal_reschedule_ref?.current?.close()}
                className={cn(
                  "py-2 px-4 text-center text-white font-medium rounded-lg hover:opacity-90",
                  `border border_${_color} text_${_color}`,
                )}
              >
                No, Cancelar
              </button>
              <button
                type="button"
                className={cn(
                  "py-2 px-4 text-center text-white font-medium rounded-lg hover:opacity-90 disabled:cursor-not-allowed",
                  `bg_${_color}`,
                )}
                disabled={Boolean(!date_selected || loading_app)}
                onClick={confirmRescheduleOrder}
              >
                Si, Reagendar
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal ref={modal_cancel_ref}>
        <div className="flex flex-col min-w-96">
          <div className={cn("w-full text-center rounded-t-xl py-2 px-6", `bg_${_color}`)}>
            <h4 className="text-lg font-medium uppercase text-white">{`Desea cancelar las siguientes órdenes?`}</h4>
          </div>
          <div className="w-full flex flex-col rounded-b-xl gap-4 p-4">
            <div className="flex flex-col flex-wrap max-w-lg gap-4 p-2">
              <p>
                {sgs_checked?.map((sg: string, i: number, sgs: Array<string>) => (
                  <span>{`${sg}${sgs.length - 1 === i ? "." : ","} `}</span>
                ))}
              </p>
              <div className="text-center font-medium">Esta acción no se puede deshacer!</div>
            </div>
            {loading_app ? (
              <div className="w-full text-center">
                <p>Espere...</p>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => modal_cancel_ref?.current?.close()}
                  className={cn(
                    "py-2 px-4 text-center text-white font-medium rounded-lg hover:opacity-90",
                    `border border_${_color} text_${_color}`,
                  )}
                >
                  No, Regresar
                </button>
                <button
                  type="button"
                  className={cn(
                    "py-2 px-4 text-center text-white font-medium rounded-lg hover:opacity-90 disabled:cursor-not-allowed",
                    `bg_${_color}`,
                  )}
                  onClick={confirmCancelOrders}
                >
                  Si, Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Fragment>
  )
}

export default ListScreen
